import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

/**
 * Theme type: 'dark' or 'light'
 */
export type Theme = 'dark' | 'light';

/**
 * Available color themes
 */
export type ColorTheme = 'blue' | 'green' | 'purple' | 'pink' | 'orange' | 'yellow';

/**
 * ThemeContext interface - defines what's available to consumers
 */
interface ThemeContextType {
  theme: Theme;               // Current theme ('dark' or 'light')
  colorTheme: ColorTheme;     // Current color theme
  toggleTheme: () => void;    // Function to toggle between themes
  setTheme: (theme: Theme) => void; // Function to set specific theme
  setColorTheme: (color: ColorTheme) => void; // Function to set specific color theme
}

// Create context with undefined default (ensures provider is used)
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// LocalStorage keys for theme persistence
const THEME_STORAGE_KEY = 'taskapp-theme';
const COLOR_THEME_STORAGE_KEY = 'taskapp_accentColor';

/**
 * ThemeProvider Component
 * @param children - React components to wrap
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  // Initialize theme from localStorage or default to 'dark'
  const [theme, setThemeState] = useState<Theme>(() => {
    // Check localStorage first
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'dark' || stored === 'light') {
      return stored;
    }
    // Default to dark mode for Linux-neon aesthetic
    return 'dark';
  });
  
  // Initialize color theme from localStorage or default to 'blue'
  const [colorTheme, setColorThemeState] = useState<ColorTheme>(() => {
    // Check localStorage first
    const stored = localStorage.getItem(COLOR_THEME_STORAGE_KEY);
    if (stored === 'blue' || stored === 'green' || stored === 'purple' || 
        stored === 'pink' || stored === 'orange' || stored === 'yellow') {
      return stored as ColorTheme;
    }
    // Default to blue
    return 'blue';
  });

  /**
   * Apply theme to document root
   */
  useEffect(() => {
    const root = window.document.documentElement;
    
    // Remove previous theme class
    root.classList.remove('light', 'dark');
    
    // Add current theme class
    root.classList.add(theme);
    
    // Persist to localStorage
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  }, [theme]);
  
  /**
   * Apply color theme to document root
   */
  useEffect(() => {
    // Apply color to root element for global theme
    document.documentElement.style.setProperty('--accent-color', `var(--neon-${colorTheme})`);
    document.documentElement.style.setProperty('--accent-color-rgb', `var(--neon-${colorTheme}-rgb)`);
    
    // Persist to localStorage
    localStorage.setItem(COLOR_THEME_STORAGE_KEY, colorTheme);
    
    // Set CSS variables for neon shadows
    document.documentElement.style.setProperty('--neon-shadow-color', `var(--neon-${colorTheme})`);
    document.documentElement.style.setProperty('--neon-glow-color', `rgba(var(--neon-${colorTheme}-rgb), 0.5)`);
  }, [colorTheme]);

  /**
   * Toggle between dark and light themes
   */
  const toggleTheme = () => {
    setThemeState(prev => prev === 'dark' ? 'light' : 'dark');
  };

  /**
   * Set a specific theme
   * @param newTheme - Theme to set ('dark' or 'light')
   */
  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
  };
  
  /**
   * Set a specific color theme
   * @param newColor - Color theme to set
   */
  const setColorTheme = (newColor: ColorTheme) => {
    setColorThemeState(newColor);
  };

  // Provide theme state and functions to all children
  return (
    <ThemeContext.Provider value={{ 
      theme, 
      colorTheme, 
      toggleTheme, 
      setTheme, 
      setColorTheme 
    }}>
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * useTheme Hook
 * 
 * @returns ThemeContextType object with theme, toggleTheme, setTheme
 * @throws Error if used outside ThemeProvider
 */
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  
  // Ensure hook is used within ThemeProvider
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  
  return context;
};
